/**
 * Data layer for the Marketing Dashboard (/marketing/dashboard).
 *
 * One hook per tab, each following the same contract:
 *   - Fetch a doubled date window in parallel (see `range.ts`), split it
 *     into the current period plus a comparison baseline, and return
 *     `totals` / `previous` so every tile can render a delta.
 *   - Expose a `connected` flag derived from *whether rows exist*, not from
 *     the `integrations` row's status. A provider can read as "connected"
 *     on the Integrations page while its first sync is still running, and a
 *     dashboard that shows a confident zero in that window is worse than
 *     one that says "waiting on the first sync".
 *   - Aggregate client-side. These rollup tables are narrow, RLS already
 *     scopes them to the caller's organization, and TanStack Query caches
 *     the result across range flips — so a range change re-slices in memory
 *     rather than hitting the network.
 *
 * Row-level security means none of these queries filter by
 * organization_id: the marketing read policies (migrations 54 / 57 / 101)
 * restrict every table to the caller's own org for super_admin, admin, and
 * manager roles.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Tables } from '@/types/database';
import {
  comparisonWindowStartIso,
  MARKETING_RANGE_DAYS,
  pctChange,
  pctChangeInverted,
  resolveAnalyticsWindow,
  safeRatio,
  splitByPeriod,
  splitByWindow,
  sumBy,
  weightedMean,
  type AnalyticsWindow,
  type MarketingRange,
} from './range';

// ---------------------------------------------------------------------------
// Website (GA4 — migration 54, `web-analytics-sync` Edge Function)
//
// Deliberately narrow: this reads only `web_sessions_daily` to produce
// headline totals *with a comparison baseline*, which the standalone Web
// Analytics page's `useWebAnalytics` (src/features/intelligence/hooks.ts)
// doesn't provide. The dashboard's Website tab pairs this with that hook —
// this one for the headline tiles and trend, that one for the traffic
// source / page / device / geo panels — instead of duplicating GA4's
// dimension rollups here.
// ---------------------------------------------------------------------------

export interface WebsiteDailyPoint {
  day: string;
  users: number;
  newUsers: number;
  sessions: number;
  engagedSessions: number;
  conversions: number;
}

export interface WebsiteTotals {
  users: number;
  newUsers: number;
  sessions: number;
  engagedSessions: number;
  conversions: number;
  engagementRate: number;
  conversionRate: number;
  /** Average engaged seconds per session across the window. */
  avgEngagementSeconds: number;
  /** Share of users that were new to the site. */
  newUserShare: number;
}

export interface WebsiteTotalsData {
  connected: boolean;
  daily: WebsiteDailyPoint[];
  totals: WebsiteTotals;
  previous: WebsiteTotals;
  latestDay: string | null;
}

type WebSessionRow = Pick<
  Tables<'web_sessions_daily'>,
  | 'day'
  | 'active_users'
  | 'new_users'
  | 'sessions'
  | 'engaged_sessions'
  | 'conversions'
  | 'user_engagement_seconds'
>;

export function useWebsiteTotals(range: MarketingRange) {
  const days = MARKETING_RANGE_DAYS[range];
  const start = comparisonWindowStartIso(days);

  return useQuery<WebsiteTotalsData>({
    queryKey: ['marketing', 'website-totals', range],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('web_sessions_daily')
        .select(
          'day, active_users, new_users, sessions, engaged_sessions, conversions, user_engagement_seconds',
        )
        .gte('day', start)
        .order('day', { ascending: true });
      if (error) throw error;

      const rows = (data ?? []) as WebSessionRow[];
      const split = splitByPeriod(rows, days);

      return {
        connected: rows.length > 0,
        daily: split.current.map((r) => ({
          day: r.day,
          users: r.active_users,
          newUsers: r.new_users,
          sessions: r.sessions,
          engagedSessions: r.engaged_sessions,
          conversions: r.conversions,
        })),
        totals: rollUpWebsiteTotals(split.current),
        previous: rollUpWebsiteTotals(split.previous),
        latestDay: rows.length > 0 ? rows[rows.length - 1].day : null,
      };
    },
  });
}

function rollUpWebsiteTotals(rows: WebSessionRow[]): WebsiteTotals {
  const users = sumBy(rows, (r) => r.active_users);
  const newUsers = sumBy(rows, (r) => r.new_users);
  const sessions = sumBy(rows, (r) => r.sessions);
  const engagedSessions = sumBy(rows, (r) => r.engaged_sessions);
  const conversions = sumBy(rows, (r) => r.conversions);
  const engagementSeconds = sumBy(rows, (r) => r.user_engagement_seconds);
  return {
    users,
    newUsers,
    sessions,
    engagedSessions,
    conversions,
    engagementRate: safeRatio(engagedSessions, sessions),
    conversionRate: safeRatio(conversions, sessions),
    avgEngagementSeconds: safeRatio(engagementSeconds, sessions),
    newUserShare: safeRatio(newUsers, users),
  };
}

// ---------------------------------------------------------------------------
// Search Console (migration 101, `search-console-sync` Edge Function)
// ---------------------------------------------------------------------------

export interface SearchDailyPoint {
  day: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

export interface SearchTotals {
  clicks: number;
  impressions: number;
  ctr: number;
  /** Impressions-weighted average position. Lower is better. */
  position: number;
}

export interface SearchRankedRow {
  key: string;
  label: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
  /** Share of total clicks in the window, 0..1. */
  share: number;
}

export interface SearchConsoleData {
  connected: boolean;
  daily: SearchDailyPoint[];
  totals: SearchTotals;
  previous: SearchTotals;
  queries: SearchRankedRow[];
  pages: SearchRankedRow[];
  devices: SearchRankedRow[];
  countries: SearchRankedRow[];
  /** Most recent day present in the data — surfaces GSC's reporting lag. */
  latestDay: string | null;
}

/** Shape shared by every `search_console_*_daily` dimension table. */
interface SearchDimensionRow {
  day: string;
  clicks: number;
  impressions: number;
  position: number;
}

export function useSearchConsole(range: MarketingRange) {
  const days = MARKETING_RANGE_DAYS[range];
  const start = comparisonWindowStartIso(days);

  return useQuery<SearchConsoleData>({
    queryKey: ['marketing', 'search-console', range],
    queryFn: async () => {
      const [totals, queries, pages, devices, countries] = await Promise.all([
        supabase
          .from('search_console_totals_daily')
          .select('day, clicks, impressions, position')
          .gte('day', start)
          .order('day', { ascending: true }),
        supabase
          .from('search_console_queries_daily')
          .select('day, query, clicks, impressions, position')
          .gte('day', start),
        supabase
          .from('search_console_pages_daily')
          .select('day, page, clicks, impressions, position')
          .gte('day', start),
        supabase
          .from('search_console_devices_daily')
          .select('day, device, clicks, impressions, position')
          .gte('day', start),
        supabase
          .from('search_console_countries_daily')
          .select('day, country, clicks, impressions, position')
          .gte('day', start),
      ]);

      const firstErr = [totals, queries, pages, devices, countries].find((r) => r.error);
      if (firstErr?.error) throw firstErr.error;

      const totalRows = (totals.data ?? []) as Array<
        Pick<Tables<'search_console_totals_daily'>, 'day' | 'clicks' | 'impressions' | 'position'>
      >;
      const queryRows = (queries.data ?? []) as Array<
        Pick<
          Tables<'search_console_queries_daily'>,
          'day' | 'query' | 'clicks' | 'impressions' | 'position'
        >
      >;
      const pageRows = (pages.data ?? []) as Array<
        Pick<
          Tables<'search_console_pages_daily'>,
          'day' | 'page' | 'clicks' | 'impressions' | 'position'
        >
      >;
      const deviceRows = (devices.data ?? []) as Array<
        Pick<
          Tables<'search_console_devices_daily'>,
          'day' | 'device' | 'clicks' | 'impressions' | 'position'
        >
      >;
      const countryRows = (countries.data ?? []) as Array<
        Pick<
          Tables<'search_console_countries_daily'>,
          'day' | 'country' | 'clicks' | 'impressions' | 'position'
        >
      >;

      const split = splitByPeriod(totalRows, days);

      const daily: SearchDailyPoint[] = split.current.map((r) => ({
        day: r.day,
        clicks: r.clicks,
        impressions: r.impressions,
        ctr: safeRatio(r.clicks, r.impressions),
        position: r.position,
      }));

      const currentDimension = <T extends SearchDimensionRow>(rows: T[]): T[] =>
        splitByPeriod(rows, days).current;

      const totalClicks = sumBy(split.current, (r) => r.clicks);

      return {
        connected: totalRows.length > 0,
        daily,
        totals: rollUpSearchTotals(split.current),
        previous: rollUpSearchTotals(split.previous),
        queries: rankSearchDimension(currentDimension(queryRows), (r) => r.query, totalClicks),
        pages: rankSearchDimension(currentDimension(pageRows), (r) => r.page, totalClicks),
        devices: rankSearchDimension(
          currentDimension(deviceRows),
          (r) => formatDeviceLabel(r.device),
          totalClicks,
        ),
        countries: rankSearchDimension(
          currentDimension(countryRows),
          (r) => countryLabel(r.country),
          totalClicks,
        ),
        latestDay: totalRows.length > 0 ? totalRows[totalRows.length - 1].day : null,
      };
    },
  });
}

function rollUpSearchTotals(rows: SearchDimensionRow[]): SearchTotals {
  const clicks = sumBy(rows, (r) => r.clicks);
  const impressions = sumBy(rows, (r) => r.impressions);
  return {
    clicks,
    impressions,
    ctr: safeRatio(clicks, impressions),
    position: weightedMean(
      rows,
      (r) => r.position,
      (r) => r.impressions,
    ),
  };
}

/**
 * Collapses a per-day dimension table into one ranked row per dimension
 * value for the whole window, sorted by clicks.
 *
 * Position is re-weighted by impressions across the grouped days rather
 * than averaged, for the reason described in `weightedMean`.
 */
function rankSearchDimension<T extends SearchDimensionRow>(
  rows: T[],
  labelFor: (row: T) => string,
  totalClicks: number,
): SearchRankedRow[] {
  const byKey = new Map<string, T[]>();
  for (const row of rows) {
    const key = labelFor(row);
    const bucket = byKey.get(key);
    if (bucket) bucket.push(row);
    else byKey.set(key, [row]);
  }

  const out: SearchRankedRow[] = [];
  for (const [key, bucket] of byKey) {
    const clicks = sumBy(bucket, (r) => r.clicks);
    const impressions = sumBy(bucket, (r) => r.impressions);
    out.push({
      key,
      label: key,
      clicks,
      impressions,
      ctr: safeRatio(clicks, impressions),
      position: weightedMean(
        bucket,
        (r) => r.position,
        (r) => r.impressions,
      ),
      share: safeRatio(clicks, totalClicks),
    });
  }

  // Clicks first, then impressions — an impression-heavy query with zero
  // clicks is still worth surfacing (it's a ranking opportunity), it just
  // shouldn't outrank a query that actually earns traffic.
  out.sort((a, b) => b.clicks - a.clicks || b.impressions - a.impressions);
  return out;
}

function formatDeviceLabel(device: string): string {
  if (!device) return 'Unknown';
  return device.charAt(0).toUpperCase() + device.slice(1).toLowerCase();
}

// Search Console reports ISO-3166-1 alpha-3 codes. We only name the
// markets Virro actually sells into; everything else renders as the
// upper-cased code, which is still readable in a ranked list.
const COUNTRY_NAMES: Record<string, string> = {
  usa: 'United States',
  can: 'Canada',
  gbr: 'United Kingdom',
  aus: 'Australia',
  deu: 'Germany',
  fra: 'France',
  nld: 'Netherlands',
  ind: 'India',
  mex: 'Mexico',
  jpn: 'Japan',
  esp: 'Spain',
  ita: 'Italy',
  bra: 'Brazil',
  irl: 'Ireland',
  nzl: 'New Zealand',
  swe: 'Sweden',
  che: 'Switzerland',
  sgp: 'Singapore',
  zaf: 'South Africa',
  phl: 'Philippines',
};

function countryLabel(code: string): string {
  return COUNTRY_NAMES[code.toLowerCase()] ?? code.toUpperCase();
}

// ---------------------------------------------------------------------------
// Organic social (migrations 57 + 101; facebook / instagram / tiktok /
// youtube sync functions)
// ---------------------------------------------------------------------------

export type SocialPlatform = 'facebook' | 'instagram' | 'tiktok' | 'youtube';

/** Fixed render order for the Social tab's sections. */
export const SOCIAL_PLATFORMS: SocialPlatform[] = [
  'facebook',
  'instagram',
  'tiktok',
  'youtube',
];

export const SOCIAL_PLATFORM_LABELS: Record<SocialPlatform, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  youtube: 'YouTube',
};

export interface SocialDailyPoint {
  day: string;
  followers: number;
  followerDelta: number;
  reach: number;
  impressions: number;
  profileViews: number;
  engagements: number;
  videoViews: number;
  watchTimeSeconds: number;
  postsPublished: number;
}

export interface SocialTotals {
  /** Latest follower snapshot in the window, not a sum. */
  followers: number;
  followerGrowth: number;
  reach: number;
  impressions: number;
  profileViews: number;
  engagements: number;
  videoViews: number;
  watchTimeSeconds: number;
  postsPublished: number;
  /** Engagements per impression (falling back to reach, then views). */
  engagementRate: number;
}

export interface SocialPostRow {
  id: string;
  platform: SocialPlatform;
  accountName: string;
  title: string | null;
  url: string | null;
  postedAt: string | null;
  reach: number;
  impressions: number;
  engagements: number;
  likes: number;
  comments: number;
  shares: number;
  saves: number;
  videoViews: number;
  watchTimeSeconds: number;
  avgViewSeconds: number;
  clicks: number;
}

export interface SocialAccountRef {
  id: string;
  name: string;
  handle: string | null;
}

export interface SocialPlatformData {
  platform: SocialPlatform;
  /** True when at least one account of this platform reported a metric row. */
  connected: boolean;
  /** True when an account exists but has no metrics yet (first sync pending). */
  awaitingFirstSync: boolean;
  accounts: SocialAccountRef[];
  daily: SocialDailyPoint[];
  totals: SocialTotals;
  previous: SocialTotals;
  topPosts: SocialPostRow[];
}

export interface SocialAnalyticsData {
  connected: boolean;
  /** Always all four platforms, in `SOCIAL_PLATFORMS` order. */
  platforms: SocialPlatformData[];
  totals: SocialTotals;
  previous: SocialTotals;
  daily: SocialDailyPoint[];
  topPosts: SocialPostRow[];
}

type SocialAccountRow = Pick<
  Tables<'social_accounts'>,
  'id' | 'provider' | 'external_id' | 'name' | 'handle'
>;
type SocialMetricRow = Pick<
  Tables<'social_account_metrics_daily'>,
  | 'social_account_id'
  | 'day'
  | 'followers'
  | 'follower_delta'
  | 'reach'
  | 'impressions'
  | 'profile_views'
  | 'engagements'
  | 'video_views'
  | 'watch_time_seconds'
  | 'posts_published'
>;
type SocialPostMetricRow = Pick<
  Tables<'social_post_metrics_daily'>,
  | 'social_account_id'
  | 'day'
  | 'post_external_id'
  | 'post_url'
  | 'posted_at'
  | 'title'
  | 'reach'
  | 'impressions'
  | 'engagements'
  | 'likes'
  | 'comments'
  | 'shares'
  | 'saves'
  | 'video_views'
  | 'watch_time_seconds'
  | 'avg_view_seconds'
  | 'clicks'
>;

export function useSocialAnalytics(range: MarketingRange | AnalyticsWindow) {
  const period = resolveAnalyticsWindow(range);

  return useQuery<SocialAnalyticsData>({
    queryKey: [
      'marketing',
      'social',
      period.previousStart,
      period.previousEnd,
      period.currentStart,
      period.currentEnd,
    ],
    queryFn: async () => {
      const [accounts, metrics, posts] = await Promise.all([
        supabase
          .from('social_accounts')
          .select('id, provider, external_id, name, handle')
          .eq('is_active', true),
        supabase
          .from('social_account_metrics_daily')
          .select(
            'social_account_id, day, followers, follower_delta, reach, impressions, profile_views, engagements, video_views, watch_time_seconds, posts_published',
          )
          .gte('day', period.previousStart)
          .order('day', { ascending: true }),
        supabase
          .from('social_post_metrics_daily')
          .select(
            'social_account_id, day, post_external_id, post_url, posted_at, title, reach, impressions, engagements, likes, comments, shares, saves, video_views, watch_time_seconds, avg_view_seconds, clicks',
          )
          .gte('day', period.previousStart),
      ]);

      const firstErr = [accounts, metrics, posts].find((r) => r.error);
      if (firstErr?.error) throw firstErr.error;

      const accountRows = (accounts.data ?? []) as SocialAccountRow[];
      const metricRows = (metrics.data ?? []) as SocialMetricRow[];
      const postRows = (posts.data ?? []) as SocialPostMetricRow[];

      // Only the four organic platforms belong on this tab. `social_accounts`
      // is also where TikTok's ads-side rows can land, and the provider enum
      // spans every integration Atlas supports.
      const accountsByPlatform = new Map<SocialPlatform, SocialAccountRow[]>();
      const platformByAccountId = new Map<string, SocialPlatform>();
      for (const account of accountRows) {
        if (!isSocialPlatform(account.provider)) continue;
        const platform = account.provider;
        platformByAccountId.set(account.id, platform);
        const bucket = accountsByPlatform.get(platform);
        if (bucket) bucket.push(account);
        else accountsByPlatform.set(platform, [account]);
      }

      const accountNameById = new Map(accountRows.map((a) => [a.id, a.name]));

      const platforms = SOCIAL_PLATFORMS.map((platform) => {
        const platformAccounts = accountsByPlatform.get(platform) ?? [];
        const accountIds = new Set(platformAccounts.map((a) => a.id));

        const platformMetrics = metricRows.filter((r) =>
          accountIds.has(r.social_account_id),
        );
        const platformPosts = postRows.filter((r) => accountIds.has(r.social_account_id));

        const metricSplit = splitByWindow(platformMetrics, period);
        const postSplit = splitByWindow(platformPosts, period);

        return {
          platform,
          connected: platformMetrics.length > 0,
          awaitingFirstSync: platformAccounts.length > 0 && platformMetrics.length === 0,
          accounts: platformAccounts.map((a) => ({
            id: a.id,
            name: a.name,
            handle: a.handle,
          })),
          daily: collapseSocialDaily(metricSplit.current),
          totals: rollUpSocialTotals(metricSplit.current),
          previous: rollUpSocialTotals(metricSplit.previous),
          topPosts: rankSocialPosts(postSplit.current, platformByAccountId, accountNameById),
        } satisfies SocialPlatformData;
      });

      const relevantMetrics = metricRows.filter((r) =>
        platformByAccountId.has(r.social_account_id),
      );
      const relevantPosts = postRows.filter((r) => platformByAccountId.has(r.social_account_id));
      const allSplit = splitByWindow(relevantMetrics, period);

      return {
        connected: platforms.some((p) => p.connected),
        platforms,
        totals: rollUpSocialTotals(allSplit.current),
        previous: rollUpSocialTotals(allSplit.previous),
        daily: collapseSocialDaily(allSplit.current),
        topPosts: rankSocialPosts(
          splitByWindow(relevantPosts, period).current,
          platformByAccountId,
          accountNameById,
        ),
      };
    },
  });
}

function isSocialPlatform(provider: string): provider is SocialPlatform {
  return (SOCIAL_PLATFORMS as string[]).includes(provider);
}

/**
 * Sums metric rows across accounts into one point per day.
 *
 * Followers is the exception: it's a point-in-time gauge per account, so
 * the daily value is the *sum across accounts* for that day (total audience),
 * never a sum across days.
 */
function collapseSocialDaily(rows: SocialMetricRow[]): SocialDailyPoint[] {
  const byDay = new Map<string, SocialDailyPoint>();
  for (const row of rows) {
    const existing = byDay.get(row.day);
    const point: SocialDailyPoint = existing ?? {
      day: row.day,
      followers: 0,
      followerDelta: 0,
      reach: 0,
      impressions: 0,
      profileViews: 0,
      engagements: 0,
      videoViews: 0,
      watchTimeSeconds: 0,
      postsPublished: 0,
    };
    point.followers += row.followers;
    point.followerDelta += row.follower_delta;
    point.reach += row.reach;
    point.impressions += row.impressions;
    point.profileViews += row.profile_views;
    point.engagements += row.engagements;
    point.videoViews += row.video_views;
    point.watchTimeSeconds += row.watch_time_seconds;
    point.postsPublished += row.posts_published;
    byDay.set(row.day, point);
  }
  return Array.from(byDay.values()).sort((a, b) => a.day.localeCompare(b.day));
}

function rollUpSocialTotals(rows: SocialMetricRow[]): SocialTotals {
  const daily = collapseSocialDaily(rows);
  const reach = sumBy(daily, (d) => d.reach);
  const impressions = sumBy(daily, (d) => d.impressions);
  const engagements = sumBy(daily, (d) => d.engagements);
  const videoViews = sumBy(daily, (d) => d.videoViews);

  // Followers is a snapshot: take the most recent day that reported one
  // rather than the last day in the window, since the final day may predate
  // the platform's own reporting lag.
  let followers = 0;
  for (let i = daily.length - 1; i >= 0; i--) {
    if (daily[i].followers > 0) {
      followers = daily[i].followers;
      break;
    }
  }

  // Denominator falls back through impressions → reach → video views so the
  // rate stays meaningful on platforms that don't report the first choice
  // (YouTube has no impressions; TikTok has no reach).
  const denominator = impressions || reach || videoViews;

  return {
    followers,
    followerGrowth: sumBy(daily, (d) => d.followerDelta),
    reach,
    impressions,
    profileViews: sumBy(daily, (d) => d.profileViews),
    engagements,
    videoViews,
    watchTimeSeconds: sumBy(daily, (d) => d.watchTimeSeconds),
    postsPublished: sumBy(daily, (d) => d.postsPublished),
    engagementRate: safeRatio(engagements, denominator),
  };
}

/**
 * Collapses per-day post rows into one row per post and ranks by
 * engagement.
 *
 * A post appears once per day it accrued metrics, and the platforms report
 * these as lifetime-to-date rather than incremental — so we keep the row
 * from the latest day per post instead of summing, which would multiply a
 * week-old post's numbers by seven.
 */
function rankSocialPosts(
  rows: SocialPostMetricRow[],
  platformByAccountId: Map<string, SocialPlatform>,
  accountNameById: Map<string, string>,
): SocialPostRow[] {
  const latestByPost = new Map<string, SocialPostMetricRow>();
  for (const row of rows) {
    const key = `${row.social_account_id}:${row.post_external_id}`;
    const existing = latestByPost.get(key);
    if (!existing || row.day > existing.day) latestByPost.set(key, row);
  }

  const out: SocialPostRow[] = [];
  for (const [key, row] of latestByPost) {
    const platform = platformByAccountId.get(row.social_account_id);
    if (!platform) continue;
    out.push({
      id: key,
      platform,
      accountName: accountNameById.get(row.social_account_id) ?? 'Unknown account',
      title: row.title,
      url: row.post_url,
      postedAt: row.posted_at,
      reach: row.reach,
      impressions: row.impressions,
      engagements: row.engagements,
      likes: row.likes,
      comments: row.comments,
      shares: row.shares,
      saves: row.saves,
      videoViews: row.video_views,
      watchTimeSeconds: row.watch_time_seconds,
      avgViewSeconds: row.avg_view_seconds,
      clicks: row.clicks,
    });
  }

  out.sort(
    (a, b) =>
      b.engagements - a.engagements ||
      b.impressions - a.impressions ||
      b.videoViews - a.videoViews,
  );
  return out;
}

// ---------------------------------------------------------------------------
// Paid ads (migration 57; amazon-ads / google-ads / meta-ads / tiktok syncs)
// ---------------------------------------------------------------------------

export type AdPlatform = 'google_ads' | 'meta_ads' | 'amazon_ads' | 'tiktok';

export const AD_PLATFORMS: AdPlatform[] = [
  'google_ads',
  'meta_ads',
  'amazon_ads',
  'tiktok',
];

export const AD_PLATFORM_LABELS: Record<AdPlatform, string> = {
  google_ads: 'Google Ads',
  meta_ads: 'Meta Ads',
  amazon_ads: 'Amazon Ads',
  tiktok: 'TikTok Ads',
};

export interface AdDailyPoint {
  day: string;
  impressions: number;
  clicks: number;
  spendCents: number;
  conversions: number;
  revenueCents: number;
}

export interface AdTotals {
  impressions: number;
  clicks: number;
  spendCents: number;
  conversions: number;
  revenueCents: number;
  ctr: number;
  cpcCents: number;
  cpaCents: number;
  /** Platform-attributed revenue ÷ spend. */
  roas: number;
}

export interface AdCampaignRow {
  key: string;
  platform: AdPlatform;
  name: string;
  impressions: number;
  clicks: number;
  spendCents: number;
  conversions: number;
  revenueCents: number;
  roas: number;
}

export interface AdPlatformData {
  platform: AdPlatform;
  connected: boolean;
  totals: AdTotals;
  previous: AdTotals;
  daily: AdDailyPoint[];
  campaigns: AdCampaignRow[];
}

export interface AdsAnalyticsData {
  connected: boolean;
  platforms: AdPlatformData[];
  totals: AdTotals;
  previous: AdTotals;
  daily: AdDailyPoint[];
  campaigns: AdCampaignRow[];
}

type AdAccountRow = Pick<Tables<'ad_accounts'>, 'id' | 'provider' | 'name'>;
type AdMetricRow = Pick<
  Tables<'ad_metrics_daily'>,
  | 'ad_account_id'
  | 'day'
  | 'campaign_external_id'
  | 'campaign_name'
  | 'impressions'
  | 'clicks'
  | 'spend_cents'
  | 'conversions'
  | 'revenue_cents'
>;

export function useAdsAnalytics(range: MarketingRange | AnalyticsWindow) {
  const period = resolveAnalyticsWindow(range);

  return useQuery<AdsAnalyticsData>({
    queryKey: [
      'marketing',
      'ads',
      period.previousStart,
      period.previousEnd,
      period.currentStart,
      period.currentEnd,
    ],
    queryFn: async () => {
      const [accounts, metrics] = await Promise.all([
        supabase.from('ad_accounts').select('id, provider, name').eq('is_active', true),
        supabase
          .from('ad_metrics_daily')
          .select(
            'ad_account_id, day, campaign_external_id, campaign_name, impressions, clicks, spend_cents, conversions, revenue_cents',
          )
          .gte('day', period.previousStart)
          .order('day', { ascending: true }),
      ]);

      if (accounts.error) throw accounts.error;
      if (metrics.error) throw metrics.error;

      const accountRows = (accounts.data ?? []) as AdAccountRow[];
      const metricRows = (metrics.data ?? []) as AdMetricRow[];

      const platformByAccountId = new Map<string, AdPlatform>();
      for (const account of accountRows) {
        if (isAdPlatform(account.provider)) {
          platformByAccountId.set(account.id, account.provider);
        }
      }

      const platforms = AD_PLATFORMS.map((platform) => {
        const platformRows = metricRows.filter(
          (r) => platformByAccountId.get(r.ad_account_id) === platform,
        );
        const split = splitByWindow(platformRows, period);
        return {
          platform,
          connected: platformRows.length > 0,
          totals: rollUpAdTotals(split.current),
          previous: rollUpAdTotals(split.previous),
          daily: collapseAdDaily(split.current),
          campaigns: rankAdCampaigns(split.current, platformByAccountId),
        } satisfies AdPlatformData;
      });

      const relevant = metricRows.filter((r) => platformByAccountId.has(r.ad_account_id));
      const split = splitByWindow(relevant, period);

      return {
        connected: relevant.length > 0,
        platforms,
        totals: rollUpAdTotals(split.current),
        previous: rollUpAdTotals(split.previous),
        daily: collapseAdDaily(split.current),
        campaigns: rankAdCampaigns(split.current, platformByAccountId),
      };
    },
  });
}

function isAdPlatform(provider: string): provider is AdPlatform {
  return (AD_PLATFORMS as string[]).includes(provider);
}

function collapseAdDaily(rows: AdMetricRow[]): AdDailyPoint[] {
  const byDay = new Map<string, AdDailyPoint>();
  for (const row of rows) {
    const point: AdDailyPoint = byDay.get(row.day) ?? {
      day: row.day,
      impressions: 0,
      clicks: 0,
      spendCents: 0,
      conversions: 0,
      revenueCents: 0,
    };
    point.impressions += row.impressions;
    point.clicks += row.clicks;
    point.spendCents += row.spend_cents;
    point.conversions += row.conversions;
    point.revenueCents += row.revenue_cents;
    byDay.set(row.day, point);
  }
  return Array.from(byDay.values()).sort((a, b) => a.day.localeCompare(b.day));
}

function rollUpAdTotals(rows: AdMetricRow[]): AdTotals {
  const impressions = sumBy(rows, (r) => r.impressions);
  const clicks = sumBy(rows, (r) => r.clicks);
  const spendCents = sumBy(rows, (r) => r.spend_cents);
  const conversions = sumBy(rows, (r) => r.conversions);
  const revenueCents = sumBy(rows, (r) => r.revenue_cents);
  return {
    impressions,
    clicks,
    spendCents,
    conversions,
    revenueCents,
    ctr: safeRatio(clicks, impressions),
    cpcCents: safeRatio(spendCents, clicks),
    cpaCents: safeRatio(spendCents, conversions),
    roas: safeRatio(revenueCents, spendCents),
  };
}

function rankAdCampaigns(
  rows: AdMetricRow[],
  platformByAccountId: Map<string, AdPlatform>,
): AdCampaignRow[] {
  const byCampaign = new Map<string, AdCampaignRow>();
  for (const row of rows) {
    const platform = platformByAccountId.get(row.ad_account_id);
    if (!platform) continue;
    // Campaign IDs are only unique within an ad account, so the account id
    // has to be part of the key or two platforms' campaign #1 would merge.
    const key = `${row.ad_account_id}:${row.campaign_external_id}`;
    const existing = byCampaign.get(key);
    const campaign: AdCampaignRow = existing ?? {
      key,
      platform,
      name: row.campaign_name ?? row.campaign_external_id,
      impressions: 0,
      clicks: 0,
      spendCents: 0,
      conversions: 0,
      revenueCents: 0,
      roas: 0,
    };
    campaign.impressions += row.impressions;
    campaign.clicks += row.clicks;
    campaign.spendCents += row.spend_cents;
    campaign.conversions += row.conversions;
    campaign.revenueCents += row.revenue_cents;
    byCampaign.set(key, campaign);
  }

  const out = Array.from(byCampaign.values());
  for (const campaign of out) {
    campaign.roas = safeRatio(campaign.revenueCents, campaign.spendCents);
  }
  out.sort((a, b) => b.spendCents - a.spendCents);
  return out;
}

// ---------------------------------------------------------------------------
// Amazon (seller orders from migration 10 + Amazon Ads from migration 57)
// ---------------------------------------------------------------------------

export interface AmazonDailyPoint {
  day: string;
  revenueCents: number;
  orders: number;
  spendCents: number;
  adRevenueCents: number;
}

export interface AmazonTotals {
  revenueCents: number;
  orders: number;
  aovCents: number;
  /** Ad spend as a share of Amazon revenue — Amazon sellers' "TACoS". */
  tacos: number;
}

export interface AmazonAnalyticsData {
  /** True when an Amazon sales channel has orders in the window. */
  connected: boolean;
  /** True when an Amazon sales channel exists but has no orders yet. */
  awaitingFirstSync: boolean;
  channels: Array<{ id: string; name: string }>;
  daily: AmazonDailyPoint[];
  totals: AmazonTotals;
  previous: AmazonTotals;
  /** Order counts by internal status, for the fulfilment health panel. */
  statusMix: Array<{ status: string; orders: number; revenueCents: number }>;
  ads: AdPlatformData | null;
}

type AmazonOrderRow = Pick<
  Tables<'sales_orders'>,
  'id' | 'sales_channel_id' | 'status' | 'ordered_at' | 'total_cents'
>;

export function useAmazonAnalytics(range: MarketingRange) {
  const days = MARKETING_RANGE_DAYS[range];
  const start = comparisonWindowStartIso(days);

  return useQuery<AmazonAnalyticsData>({
    queryKey: ['marketing', 'amazon', range],
    queryFn: async () => {
      // Amazon revenue lives on `sales_orders` (written by `amazon-sync` via
      // SP-API), keyed to a `sales_channels` row of kind 'amazon'. There can
      // legitimately be more than one — a seller account per marketplace.
      const { data: channelData, error: channelErr } = await supabase
        .from('sales_channels')
        .select('id, name')
        .eq('kind', 'amazon')
        .eq('is_active', true);
      if (channelErr) throw channelErr;

      const channels = (channelData ?? []) as Array<{ id: string; name: string }>;
      const channelIds = channels.map((c) => c.id);

      // `ordered_at` is a timestamptz, so the lower bound needs the start of
      // that UTC day rather than the bare date string.
      const [orders, adAccounts] = await Promise.all([
        channelIds.length > 0
          ? supabase
              .from('sales_orders')
              .select('id, sales_channel_id, status, ordered_at, total_cents')
              .in('sales_channel_id', channelIds)
              .gte('ordered_at', `${start}T00:00:00Z`)
              .order('ordered_at', { ascending: true })
          : Promise.resolve({ data: [], error: null }),
        supabase
          .from('ad_accounts')
          .select('id, provider, name')
          .eq('provider', 'amazon_ads')
          .eq('is_active', true),
      ]);

      if (orders.error) throw orders.error;
      if (adAccounts.error) throw adAccounts.error;

      const orderRows = (orders.data ?? []) as AmazonOrderRow[];
      const amazonAdAccounts = (adAccounts.data ?? []) as AdAccountRow[];

      let adMetricRows: AdMetricRow[] = [];
      if (amazonAdAccounts.length > 0) {
        const { data, error } = await supabase
          .from('ad_metrics_daily')
          .select(
            'ad_account_id, day, campaign_external_id, campaign_name, impressions, clicks, spend_cents, conversions, revenue_cents',
          )
          .in(
            'ad_account_id',
            amazonAdAccounts.map((a) => a.id),
          )
          .gte('day', start)
          .order('day', { ascending: true });
        if (error) throw error;
        adMetricRows = (data ?? []) as AdMetricRow[];
      }

      // Cancelled orders are excluded from revenue but still reported in the
      // status mix, so the team can see cancellation rate as its own signal
      // rather than as a silent gap in revenue.
      const billable = orderRows.filter((o) => o.status !== 'cancelled');
      const dayOf = (row: AmazonOrderRow) => row.ordered_at.slice(0, 10);

      const orderSplit = splitByPeriod(
        billable.map((o) => ({ ...o, day: dayOf(o) })),
        days,
      );
      const adPlatformByAccount = new Map<string, AdPlatform>(
        amazonAdAccounts.map((a) => [a.id, 'amazon_ads' as AdPlatform]),
      );
      const adSplit = splitByPeriod(adMetricRows, days);

      const currentAdTotals = rollUpAdTotals(adSplit.current);
      const previousAdTotals = rollUpAdTotals(adSplit.previous);

      const statusCounts = new Map<string, { orders: number; revenueCents: number }>();
      for (const order of orderRows) {
        const bucket = statusCounts.get(order.status) ?? { orders: 0, revenueCents: 0 };
        bucket.orders += 1;
        bucket.revenueCents += order.total_cents;
        statusCounts.set(order.status, bucket);
      }

      return {
        connected: orderRows.length > 0,
        awaitingFirstSync: channels.length > 0 && orderRows.length === 0,
        channels,
        daily: buildAmazonDaily(orderSplit.current, adSplit.current),
        totals: rollUpAmazonTotals(orderSplit.current, currentAdTotals.spendCents),
        previous: rollUpAmazonTotals(orderSplit.previous, previousAdTotals.spendCents),
        statusMix: Array.from(statusCounts.entries())
          .map(([status, bucket]) => ({ status, ...bucket }))
          .sort((a, b) => b.orders - a.orders),
        ads:
          amazonAdAccounts.length > 0
            ? {
                platform: 'amazon_ads',
                connected: adMetricRows.length > 0,
                totals: currentAdTotals,
                previous: previousAdTotals,
                daily: collapseAdDaily(adSplit.current),
                campaigns: rankAdCampaigns(adSplit.current, adPlatformByAccount),
              }
            : null,
      };
    },
  });
}

function buildAmazonDaily(
  orders: Array<AmazonOrderRow & { day: string }>,
  adRows: AdMetricRow[],
): AmazonDailyPoint[] {
  const byDay = new Map<string, AmazonDailyPoint>();
  const ensure = (day: string): AmazonDailyPoint => {
    const existing = byDay.get(day);
    if (existing) return existing;
    const point: AmazonDailyPoint = {
      day,
      revenueCents: 0,
      orders: 0,
      spendCents: 0,
      adRevenueCents: 0,
    };
    byDay.set(day, point);
    return point;
  };

  for (const order of orders) {
    const point = ensure(order.day);
    point.revenueCents += order.total_cents;
    point.orders += 1;
  }
  for (const row of adRows) {
    const point = ensure(row.day);
    point.spendCents += row.spend_cents;
    point.adRevenueCents += row.revenue_cents;
  }

  return Array.from(byDay.values()).sort((a, b) => a.day.localeCompare(b.day));
}

function rollUpAmazonTotals(
  orders: Array<AmazonOrderRow & { day: string }>,
  adSpendCents: number,
): AmazonTotals {
  const revenueCents = sumBy(orders, (o) => o.total_cents);
  return {
    revenueCents,
    orders: orders.length,
    aovCents: safeRatio(revenueCents, orders.length),
    tacos: safeRatio(adSpendCents, revenueCents),
  };
}

// ---------------------------------------------------------------------------
// Shared delta helpers re-exported so tab components don't need to reach
// into `range.ts` for the two functions they actually use.
// ---------------------------------------------------------------------------

export { pctChange, pctChangeInverted };
